<template>
  <main>
    <div class="lesson__white-section">
      <YContainer>
        <z-breadcrumb
          :items="items"
          class="lesson__breadcrumb"
        />

        <MontillaSlot
          slug="enem-lesson-top"
          :tags="slotTags"
        />

        <LessonHeader
          :title="lesson.title"
          :author-full-name="authorFullName"
          :author-slug="authorSlug"
          :show-date="showDate"
          :category-name="category.name"
        />
      </YContainer>
    </div>

    <YContainer class="lesson__container">
      <SummaryCard
        v-if="isMobile"
        :summary="summary"
        class="lesson__index lesson__area-aside"
      />

      <div
        v-else
        class="lesson__area-aside"
      >
        <LessonLeadCollector
          :source="leadSource"
          :custom-event-attributes="customLeadEventAttributes"
        />

        <SummaryCard
          :summary="[...summary]"
          class="lesson__index"
        />
      </div>

      <section class="lesson__content lesson__area-content">
        <section
          v-for="item in filteredIntroduction"
          :key="item.id"
          class="lesson__section-introduction"
        >
          <z-title
            :id="item.id"
            tag="h2"
            size="medium"
            class="lesson__section-title"
          >
            {{ item.title }}
          </z-title>

          <z-dynamic-content>
            <div
              class="lesson__section-content"
              v-html="item.content"
            />
          </z-dynamic-content>
        </section>

        <section
          v-if="takeaways && takeaways.length"
          class="lesson__takeaways"
        >
          <LessonTakeaways :takeaways="takeaways" />
        </section>

        <div class="lesson__section">
          <MontillaSlot
            slug="enem-lesson-middle"
            :tags="slotTags"
          />
        </div>

        <section
          v-for="item in removeIntroduction"
          :key="item.id"
          class="lesson__section"
        >
          <z-title
            :id="item.id"
            tag="h2"
            size="small"
            weight="normal"
            class="lesson__section-title"
          >
            {{ item.title }}
          </z-title>

          <z-dynamic-content>
            <div
              class="lesson__section-content"
              v-html="item.content"
            />
          </z-dynamic-content>
        </section>

        <section
          v-if="lesson.videos && lesson.videos.length"
          class="lesson__section"
        >
          <div class="lesson__section-content">
            <LessonVideoGallery :videos="lesson.videos" />
          </div>
        </section>

        <section
          v-if="lesson.exercises && lesson.exercises.length"
          id="exercises"
          class="lesson__section"
        >
          <div class="lesson__section-content">
            <LessonExercises
              :exercises="lesson.exercises"
              :lesson-title="lesson.title"
            />
          </div>
        </section>

        <section
          class="lesson__section"
        >
          <div class="lesson__section-content">
            <MiniBio
              v-if="showMiniBio"
              class="mini__bio-content"
              :author-name="authorFullName"
              :author-image="authorAvatar"
              :author-biography="authorBiography"
            />
          </div>
        </section>

        <section
          v-if="isMobile"
          class="lesson__section"
        >
          <LessonLeadCollector
            class="lesson__lead-collector"
            :source="leadSource"
            :custom-event-attributes="customLeadEventAttributes"
          />
        </section>

        <section
          v-if="relatedLessons.length"
          class="lesson__section"
        >
          <RelatedLessons
            :related-lessons="relatedLessons"
            :category-slug="category.slug"
          />
        </section>

        <LeadCollectorSection
          visitable-type="aaa"
        />
      </section>
    </YContainer>

    <div
      v-if="posts && posts.length"
      class="lesson__white-section lesson__related-posts"
    >
      <YContainer>
        <RelatedPosts
          :posts="posts"
          title="Conteúdos relacionados"
          category="Enem"
        />
      </YContainer>
    </div>

    <MontillaSlot
      slug="enem-modal"
      :tags="slotTags"
    />
  </main>
</template>

<script>
import { YContainer } from '@quero/yeti'

import LessonVideoGallery from '~/components/lesson/lesson-video-gallery'
import LessonExercises from '~/components/lesson/lesson-exercises'
import LeadCollectorSection from '~/components/lead-collector/lead-collector-section'

import { parseLessonContent } from '~/assets/js/services/lessons'
import { getLessonsBySlug, getLessonsByTags } from '~/assets/js/services/graphqlApi'
import { retry } from '~/assets/js/utils/http'

import MontillaSlot from '~/components/montilla-slot'
import { lessonSlotTags } from '~/utils/montilla-slot-tags'
import MiniBio from '~/components/lesson/mini-bio'
import LessonHeader from '~/components/lesson/lesson-header.vue'
import SummaryCard from '~/components/lesson/summary-card/index.vue'
import RelatedLessons from '~/components/lesson/related-lessons.vue'
import LessonTakeaways from '~/components/lesson/lesson-takeaways.vue'
import RelatedPosts from '~/components/lesson/related-posts/index.vue'
import LessonLeadCollector from '~/components/lesson/lead-collector/index.vue'
import {
  generateGraphSchema,
  generateArticleSchema,
  generateBreadcrumbSchema,
  generateOrganizationSchema,
  generatePersonSchema,
} from '~/utils/schemas'
import { slugify } from '~/assets/js/utils/slug'

export default {
  name: 'LessonIndex',
  components: {
    MontillaSlot,
    LessonExercises,
    LessonVideoGallery,
    YContainer,
    LeadCollectorSection,
    MiniBio,
    LessonHeader,
    SummaryCard,
    RelatedLessons,
    LessonTakeaways,
    RelatedPosts,
    LessonLeadCollector,
  },
  async asyncData ({ $cms, error, params }) {
    try {
      let query = getLessonsBySlug(params.lesson)
      let graphqlQuery = {
        query,
      }
      const lesson = await retry(
        () => $cms.$post('/graphql', graphqlQuery),
        { retries: 10 },
      ).then((response) => {
        return response.data.ManualDoEnemAulas[0]
      })

      const { author, category } = lesson

      let tags = ''

      lesson.tags.forEach((tag) => {
        tags = tags + `"${tag.ManualDoEnemTags_id.name}",`
      })

      tags = tags.replace(/,\s*$/, '')

      const hasExercises = Boolean(lesson.exercises?.length)
      const { summary, contents } = parseLessonContent(lesson.text, hasExercises)

      let relatedLessons = []

      if (tags) {
        query = getLessonsByTags(tags)

        graphqlQuery = {
          query,
        }

        relatedLessons = await retry(
          () => $cms.$post('/graphql', graphqlQuery),
          { retries: 10 },
        ).then((response) => {
          return response.data.ManualDoEnemAulas
        })
      }

      const takeawaysRequest = await retry(
        () => $cms.$get(
          '/items/enem_takeaways',
          {
            params:
              {
                fields: '*',
                'filter[lesson_slug][_eq]': params.lesson,
              },
          },
        ),
        { retries: 2 },
      ).catch(() => {
        return {}
      })

      return {
        lesson,
        category,
        author,
        summary,
        contents,
        relatedLessons: relatedLessons
          .slice(0, 4),
        takeaways: takeawaysRequest.data?.[0]?.takeaways,
      }
    } catch (err) {
      error({
        statusCode: 500,
        message: `Failed to fetch lesson data for ${params.lesson}, error: ${err}`,
      })
    }
  },
  data () {
    return {
      lesson: {},
      category: {},
      author: {},
      summary: [],
      contents: [],
      relatedLessons: [],
      takeaways: [],
      leadSource: 'manual-do-enem-lessons',
      posts: [],
      isMobile: false,
    }
  },
  head () {
    return {
      title: `${this.lesson.title} - Manual do Enem`,
      meta: [
        { hid: 'description', name: 'description', content: this.lesson.meta_description || 'Aqui no Manual do Enem, você encontra conteúdos, videos e exercícios relacionados ao Enem e Vestibulares. Aprenda Matemática, Redação, Biologia, Português, etc.' },
      ],
      script: [
        {
          src: 'https://cdnjs.cloudflare.com/ajax/libs/mathjax/2.7.5/MathJax.js?config=TeX-MML-AM_CHTML',
          async: true,
          defer: true,
        },
        {
          type: 'application/ld+json',
          json: generateGraphSchema(
            generateBreadcrumbSchema(this.items),
            generateArticleSchema({
              title: this.lesson.title,
              publisher: this.authorFullName,
              publisherUrl: `https://querobolsa.com.br/revista/autor/${this.authorSlug}`,
              datePublished: this.lesson.date_created,
              dateModified: this.lesson.date_updated,
            }, this.$route.path),
            generatePersonSchema({
              name: this.authorFullName,
              url: `https://querobolsa.com.br/revista/autor/${this.authorSlug}`,
            }, this.$route.path),
            generateOrganizationSchema(),
          ),
        },
      ],
    }
  },
  computed: {
    slotTags () {
      return lessonSlotTags({ category: this.category, lesson: this.lesson })
    },
    authorSlug () {
      if (!this.author) return null

      return slugify(this.authorFullName)
    },
    authorFullName () {
      if (!this.author) return null
      return `${this.author.name} ${this.author.surname}`.trim()
    },
    authorAvatar () {
      return (this.author?.avatar)
        ? this.$mediaHub(this.author?.avatar)
        : null
    },
    authorBiography () {
      return this.author?.biography
    },
    showMiniBio () {
      return this.authorFullName && this.authorBiography
    },
    showDate () {
      const latestDate = this.lesson.date_created < this.lesson.date_updated
        ? this.lesson.date_updated
        : this.lesson.date_created

      const dateObj = new Date(latestDate)

      if (!isNaN(dateObj)) {
        return dateObj.getDate() + '/' + (dateObj.getMonth() + 1) + '/' + dateObj.getFullYear()
      }

      return ''
    },
    filteredIntroduction () {
      return this.contents.filter(item => item.title === 'Introdução')
    },
    removeIntroduction () {
      return this.contents.filter(item => item.title !== 'Introdução')
    },
    customLeadEventAttributes () {
      return { lesson: this.lesson.slug }
    },
    items () {
      return [
        { text: 'Home', url: 'https://querobolsa.com.br/' },
        { text: 'Manual do Enem', url: '/enem/manual-do-enem' },
        { text: this.category.name, url: `/enem/${this.category.slug}` },
        { text: this.lesson.title },
      ]
    },
  },
  beforeMount () {
    this.isMobile = window.matchMedia('(max-width: 768px)').matches
  },
  mounted () {
    MathJax.Hub.Config({
      CommonHTML: { linebreaks: { automatic: true } },
    })

    MathJax.Hub.Typeset()
    window.montilla.track('content_view', { content_view_name: 'enem-aula' })
  },
}
</script>

<style lang="scss" scoped>
.lesson__white-section {
  background-color: var(--color-white);
}

.lesson__container {
  display: grid;
  grid-template-areas: (
  'aside'
  'content'
  );
  grid-template-columns: auto;
  padding-bottom: var(--unit-1600);

  @media (min-width: $breakpoint-tablet) {
    column-gap: var(--unit-1400);
    grid-template-areas: (
      'content aside'
      'content aside'
    );
    grid-template-columns: 2fr 1fr;
    padding-bottom: 0;
  }
}

.lesson__index {
  grid-area: index;
  margin: var(--unit-0600) 0 0;
  position: sticky;
  top: var(--unit-0400);
  z-index: var(--z-index-overlay);

  @media (min-width: $breakpoint-tablet) {
    top: var(--unit-1600);
  }
}

.lesson__content {
  grid-area: content;
}

.lesson__section {
  margin-bottom: var(--unit-1000);

  @media (min-width: $breakpoint-tablet) {
    margin-bottom: var(--unit-1600);
  }
}

.lesson__section-introduction {
  margin-bottom: var(--unit-1000);

  @media (min-width: $breakpoint-tablet) {
    margin-bottom: var(--unit-1600);
  }
}

.lesson__takeaways {
  margin: var(--unit-1000) 0;
}

.lesson__section-title {
  margin: 0 0 var(--unit-0300);

  font-size: 1.5rem;

  @media (min-width: $breakpoint-tablet) {
    font-size: 1.75rem;
  }
}

.lesson__area-content {
  grid-area: content;
  margin: 0;

  @media (min-width: $breakpoint-tablet) {
    margin: var(--unit-1200) 0 var(--unit-1600);
  }
}

.lesson__area-aside {
  grid-area: aside;
  margin: var(--unit-1000) 0;

  @media (min-width: $breakpoint-tablet) {
    margin: var(--unit-1200) 0 var(--unit-1600);
  }
}

.lesson__section-content::v-deep {
  & p:first-child {
    margin-top: 0;
  }

  & p:last-child {
    margin-bottom: 0;
  }

  & .fr-img-caption {
    display: block;
    margin-bottom: var(--unit-0800);
    margin-top: var(--unit-0800);
    text-align: center;
    width: 100% !important;
  }

  & .fr-img-wrap {
    display: block;
    margin-left: auto;
    margin-right: auto;
    max-width: 100%;

    & img {
      max-width: 100%;
    }
  }

  & .fr-inner {
    font-weight: var(--font-weight-medium);
    text-transform: capitalize;
  }

  & table {
    display: block;
    max-width: fit-content;
    overflow-x: auto;
  }

  & img {
    border-radius: var(--unit-0200);
    max-width: 100%;
  }
}

.lesson__related-posts {
  padding: var(--unit-0600) 0;

  @media (min-width: $breakpoint-tablet) {
    padding: var(--unit-1200) 0 var(--unit-1600);
  }
}
</style>
